import { PageContainer } from '@/components/PageContainer';
import { MarkdownMessage } from '@astro/shared-ui';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Heading } from '@televet/kibble-ui/Heading';
import { Spinner } from '@televet/kibble-ui/Spinner';
import { Text } from '@televet/kibble-ui/Text';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, VStack, Box } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

export const Route = createFileRoute(
  '/_authenticated/test-suites/$suiteId/executions/$executionId'
)({
  component: RouteComponent,
  beforeLoad: () => {
    return {
      pageTitle: 'Test Execution Results',
    };
  },
});

function RouteComponent() {
  const { executionId, suiteId } = Route.useParams();
  const navigate = useNavigate();

  const { data: executionData, refetch } = useQuery(
    trpc.agentTest.getExecution.queryOptions({ executionId })
  );

  const execution = executionData?.execution;

  // Poll for updates if execution is still running
  useEffect(() => {
    if (execution?.status === 'Running') {
      const interval = setInterval(() => {
        refetch();
      }, 5000); // Poll every 5 seconds

      return () => clearInterval(interval);
    }
  }, [execution?.status, refetch]);

  if (!execution) {
    return (
      <PageContainer>
        <Flex justify="center" align="center" h="400px">
          <Spinner size="xl" />
        </Flex>
      </PageContainer>
    );
  }

  // Calculate status from expectations
  const allPassed = execution.testResults.every((result) => {
    if (result.testResultExpectations.length === 0) return false;
    return result.testResultExpectations.every((re) => re.passed);
  });
  const allFailed = execution.testResults.every((result) => {
    if (result.testResultExpectations.length === 0) return false;
    return result.testResultExpectations.every((re) => !re.passed);
  });
  const somePassed = execution.testResults.some((result) => {
    if (result.testResultExpectations.length === 0) return false;
    return result.testResultExpectations.some((re) => re.passed);
  });

  return (
    <PageContainer>
      <Flex direction="column" gap={6} w="100%" maxW="6xl">
        <Card borderColor="border.default" p={2}>
          <Box px={6} pt={6}>
            <Flex justify="space-between" align="center">
              <Box>
                <Heading size="lg">Test Execution Results</Heading>
                <Text fontSize="sm" variant="subtle" mt={1}>
                  Suite: {execution.testSuite.name}
                </Text>
              </Box>
              <Flex gap={2}>
                <Tag
                  label={
                    execution.status === 'Running'
                      ? 'Running...'
                      : execution.status === 'Completed'
                        ? allPassed
                          ? 'All Passed'
                          : allFailed
                            ? 'All Failed'
                            : 'Partial Pass'
                        : 'Failed'
                  }
                  variant={
                    execution.status === 'Completed'
                      ? allPassed
                        ? 'success'
                        : somePassed
                          ? 'warning'
                          : 'error'
                      : execution.status === 'Failed'
                        ? 'error'
                        : 'info'
                  }
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate({ to: `/test-suites/${suiteId}` })}
                >
                  Back to Suite
                </Button>
              </Flex>
            </Flex>
          </Box>
          <Box px={6} pb={6}>
            <VStack gap={2} align="stretch">
              <Flex gap={4}>
                <Text fontSize="sm">
                  <strong>Agent:</strong> {execution.agent?.name || '—'}
                </Text>
                {execution.agentVersionNumber && (
                  <Text fontSize="sm">
                    <strong>Version:</strong> {execution.agentVersionNumber}
                  </Text>
                )}
                {execution.startedAt && (
                  <Text fontSize="sm">
                    <strong>Started:</strong>{' '}
                    {new Date(execution.startedAt).toLocaleString()}
                  </Text>
                )}
                {execution.completedAt && (
                  <Text fontSize="sm">
                    <strong>Completed:</strong>{' '}
                    {new Date(execution.completedAt).toLocaleString()}
                  </Text>
                )}
              </Flex>
              <Text fontSize="sm">
                <strong>Test Cases:</strong> {execution.testResults.length}
              </Text>
            </VStack>
          </Box>
        </Card>

        {execution.status === 'Running' && (
          <Card borderColor="border.default" p={2}>
              <Flex justify="center" align="center" py={8}>
                <VStack gap={4}>
                  <Spinner size="xl" />
                  <Text>Test execution in progress...</Text>
                </VStack>
              </Flex>
          </Card>
        )}

        <VStack gap={4} align="stretch">
          {execution.testResults.map((result) => {
            const passedExpectations = result.testResultExpectations.filter(
              (re) => re.passed
            ).length;
            const totalExpectations = result.testResultExpectations.length;
            const allExpectationsPassed =
              totalExpectations > 0 && passedExpectations === totalExpectations;
            const resultStatus = allExpectationsPassed
              ? 'Passed'
              : totalExpectations > 0 && passedExpectations === 0
                ? 'Failed'
                : totalExpectations > 0
                  ? 'Partial'
                  : 'Pending';

            return (
              <Card
                key={result.id}
                borderColor={
                  resultStatus === 'Passed'
                    ? 'green.500'
                    : resultStatus === 'Failed'
                      ? 'red.500'
                      : 'border.default'
                }
                borderWidth="2px"
              >
                <Box px={6} pt={6}>
                  <Flex justify="space-between" align="center">
                    <Box>
                      <Heading size="md">{result.testCase.name}</Heading>
                      {result.testCase.description && (
                        <Text fontSize="sm" variant="subtle" mt={1}>
                          {result.testCase.description}
                        </Text>
                      )}
                    </Box>
                    <Tag
                      label={resultStatus}
                      variant={
                        resultStatus === 'Passed'
                          ? 'success'
                          : resultStatus === 'Failed'
                            ? 'error'
                            : 'default'
                      }
                    />
                  </Flex>
                </Box>
                <Box px={6} pb={6}>
                  <VStack gap={4} align="stretch">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Input Prompt:
                      </Text>
                      <Card size="sm" bg="bg.subtle">
                        <Box p={6}>
                          <Text fontSize="sm">
                            {result.testCase.inputPrompt}
                          </Text>
                        </Box>
                      </Card>
                    </Box>

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Agent Response:
                      </Text>
                      <Card size="sm" bg="bg.subtle">
                        <Box p={6}>
                          <MarkdownMessage
                            content={result.actualResponse || 'No response yet'}
                          />
                        </Box>
                      </Card>
                    </Box>

                    {result.aiValidationFeedback && (
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          mb={2}
                          variant="warning"
                        >
                          AI Validation Feedback:
                        </Text>
                        <Card
                          size="sm"
                          bg="orange.50"
                          borderColor="orange.200"
                        >
                          <Box p={6}>
                            <Text fontSize="sm" variant="warning">
                              {result.aiValidationFeedback}
                            </Text>
                          </Box>
                        </Card>
                      </Box>
                    )}

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Expectations ({passedExpectations}/{totalExpectations}{' '}
                        passed):
                      </Text>
                      {result.testResultExpectations.length === 0 ? (
                        <Text fontSize="xs" variant="subtle">
                          No expectations evaluated
                        </Text>
                      ) : (
                        <VStack gap={2} align="stretch">
                          {result.testResultExpectations.map((re) => (
                            <Card
                              key={re.id}
                              borderWidth="1px"
                              borderColor={re.passed ? 'green.200' : 'red.200'}
                              bg={re.passed ? 'green.50' : 'red.50'}
                              size="sm"
                            >
                              <Box p={6}>
                                <Flex justify="space-between" align="start">
                                  <Box flex="1">
                                    <Flex gap={2} align="center" mb={1}>
                                      <Tag
                                        label={re.testExpectation.type}
                                        variant={
                                          re.testExpectation.type === 'Content'
                                            ? 'info'
                                            : re.testExpectation.type === 'Format'
                                              ? 'default'
                                              : re.testExpectation.type === 'Tone'
                                                ? 'warning'
                                                : 'success'
                                        }
                                      />
                                      <Tag
                                        label={re.passed ? 'Passed' : 'Failed'}
                                        variant={re.passed ? 'success' : 'error'}
                                      />
                                    </Flex>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {re.testExpectation.criteria}
                                    </Text>
                                    {re.testExpectation.description && (
                                      <Text fontSize="xs" variant="subtle">
                                        {re.testExpectation.description}
                                      </Text>
                                    )}
                                    {re.feedback && (
                                      <Text
                                        fontSize="xs"
                                        variant={
                                          re.passed ? 'success' : 'danger'
                                        }
                                        mt={2}
                                      >
                                        <strong>Feedback:</strong> {re.feedback}
                                      </Text>
                                    )}
                                  </Box>
                                </Flex>
                              </Box>
                            </Card>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </Box>
              </Card>
            );
          })}
        </VStack>
      </Flex>
    </PageContainer>
  );
}
